import React, { useEffect, useState } from "react";
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EastIcon from "@mui/icons-material/East"; // Import arrow icon
import { ReactComponent as LeftArrow } from "../../../assets/left-arrow-calendar.svg";
import { ReactComponent as RightArrow } from "../../../assets/rigth-arrow-calendar.svg";
import useWindowSize from "../../../hooks/useWindowSize";

interface Job {
  id: string;
  title: string;
  company: string;
  logoUrl: string;
  jobUrl: string;
  jobPublishedAt: string;
  isPublished: boolean;
  publishedAt: string | null;
  locations?: { shortName: string }[];
  properties?: {
    employmentTypes: {
      fullTime: boolean;
      partTime: boolean;
      internship: boolean;
      contractor: boolean;
      apprenticeship: boolean;
      temporary: boolean;
      workingStudent: boolean;
    };
    jobLevels: {
      entryLevel: boolean;
      experienced: boolean;
      manager: boolean;
      director: boolean;
      executive: boolean;
    };
  };
}
interface RecommendationJobProps {
  job: Job;
  onJobSelect: (jobId: string) => void;
  isSelected?: boolean;
}
const RecommendationJob: React.FC<RecommendationJobProps> = ({
  job,
  onJobSelect,
  isSelected = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("jobs");

  const getTimeAgo = (dateString: string) => {
    const publishDate = new Date(dateString);
    const now = new Date();

    const publishDateDay = new Date(publishDate.setHours(0, 0, 0, 0));
    const todayDay = new Date(now.setHours(0, 0, 0, 0));

    const diffTime = todayDay.getTime() - publishDateDay.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t("today") || "Today";
    if (diffDays === 1) return t("yesterday") || "Yesterday";
    return t("days-ago", { count: diffDays }) || `${diffDays} days ago`;
  };

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(job.jobUrl, "_blank");
  };

  const handleContainerClick = () => {
    onJobSelect(job.id);
  };

  const locations = job.locations || [];
  const locationsList = locations
    .slice(0, 3)
    .map((loc) => loc.shortName)
    .join(", ");

  const jobTypesAndLevels = [
    ...Object.entries(job.properties?.employmentTypes || {})
      .filter(([_, value]) => value === true)
      .map(([key]) => ({ key, type: "employmentType" })),

    ...Object.entries(job.properties?.jobLevels || {})
      .filter(([_, value]) => value === true)
      .map(([key]) => ({ key, type: "jobLevel" })),
  ].slice(0, 3);

  return (
    <div className="">
      <div
        className={`h-[235px] p-4 border ${
          isSelected ? "border-Yellow-default border" : "border-Grey-light-2"
        } rounded-lg overflow-hidden bg-Grey-light-3 relative cursor-pointer`}
        onClick={handleContainerClick}
      >
        <div className="flex">
          <div className="w-10 h-10 mr-4 flex-shrink-0">
            {job.logoUrl ? (
              <img
                className="w-full h-full object-contain rounded-md"
                src={job.logoUrl}
                alt={job.company}
              />
            ) : (
              <div className="w-full h-full bg-white rounded-md" />
            )}
          </div>
          <div className="flex flex-col">
            <p className="font-bold line-clamp-2 mb-1">{job.title}</p>
            <p className="text-sm font-medium mb-2">{job.company}</p>

            {/* Location with icon */}
            {locations.length > 0 && (
              <div className="flex items-center mt-1 text-sm text-Grey-medium">
                <LocationOnIcon fontSize="small" className="mr-1" />
                <span>{locationsList}</span>
              </div>
            )}

            {/* Job types and levels (up to 3 combined) */}
            {jobTypesAndLevels.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1">
                {jobTypesAndLevels.map((item, index) => (
                  <span
                    key={`${item.type}-${item.key}`}
                    className="text-xs bg-gray-200 px-2 py-0.5 rounded"
                  >
                    {item.type === "employmentType"
                      ? t(`employment-types.${item.key}`) || item.key
                      : t(`job-levels.${item.key}`) || item.key}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-3 left-0 right-0 px-4 flex justify-between items-center">
          <p className="text-xs ml-14 text-Grey-medium">
            {getTimeAgo(job.jobPublishedAt)}
          </p>
          <div className="flex items-center">
            <button
        
              className="flex items-center justify-center text-Yellow-default  rounded-full  text-xs font-semibold"
              aria-label="View job details"
            >
             
              <EastIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface RecommendationJobsProps {
  jobsData: Job[];
}

interface RecommendationJobsProps {
  jobsData: Job[];
  onJobSelect: (jobId: string) => void;
}

const RecommendationJobs: React.FC<RecommendationJobsProps> = ({
  jobsData,
  onJobSelect,
}) => {
  const { t } = useTranslation("jobs");
  const [slidesNumber, setSlidesNumber] = useState(3);
  const windowSize = useWindowSize();
  const [selectedJobId, setSelectedJobId] = useState("");
  const handleLocalJobSelect = (jobId: string) => {
    setSelectedJobId(jobId);
    onJobSelect(jobId);
  };

  useEffect(() => {
    const width = windowSize || 1440;

    if (width >= 1640 && slidesNumber !== 3) {
      setSlidesNumber(3);
    } else if (width < 1640 && width >= 1140 && slidesNumber !== 2) {
      setSlidesNumber(2);
    } else if (width < 1140 && width >= 1024 && slidesNumber !== 1) {
      setSlidesNumber(1);
    } else if (width < 1024 && width >= 900 && slidesNumber !== 3) {
      setSlidesNumber(3);
    } else if (width < 900 && width >= 640 && slidesNumber !== 2) {
      setSlidesNumber(2);
    } else if (width < 640 && slidesNumber !== 1) {
      setSlidesNumber(1);
    }
  }, [windowSize, slidesNumber]);

  if (!jobsData || jobsData.length === 0) {
    return null;
  }

  return (
    <>
      <p className="text-base text-Grey-dark font-bold ">
        {t("recommended-jobs") || "Recommended Jobs"}
      </p>
      <div className="w-full relative">
        <CarouselProvider
          naturalSlideWidth={5}
          naturalSlideHeight={8}
          totalSlides={jobsData.length || 0}
          visibleSlides={slidesNumber}
          isIntrinsicHeight
        >
          <ButtonBack>
            <button className="absolute top-[125px] left-[-5px]  z-10 bg-white border border-black rounded-lg flex justify-center items-center">
              <LeftArrow className="h-6 w-6 p-2  " />
            </button>
          </ButtonBack>
          <Slider>
            {jobsData.map((job, idx) => {
              return (
                <Slide key={job.id} index={idx}>
                  <div className="px-2">
                    <RecommendationJob
                      job={job}
                      onJobSelect={handleLocalJobSelect}
                      isSelected={job.id === selectedJobId}
                    />
                  </div>
                </Slide>
              );
            })}
          </Slider>

          <ButtonNext>
            <button className="absolute top-[125px] right-[-5px]  z-10 bg-white border border-black rounded-lg flex justify-center items-center">
              <RightArrow className="h-6 w-6 p-2" />
            </button>
          </ButtonNext>
        </CarouselProvider>
      </div>
      <p className="text-base text-Grey-dark font-bold mb-5 ">
        {t("your-search") || "Your Search"}
      </p>
   
    </>
  );
};

export default RecommendationJobs;
